import { useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, BadgeCheck, CircleCheckBig } from 'lucide-react';
import { Button } from '@siliconx/shared/ui/Button';
import { SerialEntryPrompt } from './SerialEntryNudge';

/**
 * SIL-1183: the individual-org Add Asset capture-success moment. Replaces
 * the whole form (method picker + stepper + form) - not a toast, no
 * redirect to /submissions (that's the business-org flow, untouched - see
 * routes/assets/submit.tsx's mutation onSuccess).
 */
export interface AddAssetSuccessPanelProps {
  /** "Asset captured." or "N assets captured." */
  headline: string;
  /** What was captured, e.g. "Dell PowerEdge R750 · SN-4471-A" or "Supermicro SYS-821GE · 8 GPUs". */
  itemLabel: string;
  /** TrueRead captures carry the "Verified by TrueRead probe" badge. */
  verifiedByTrueRead: boolean;
  /** Resets the form and returns to the method view, on the remembered method. */
  onAddAnother: () => void;
}

export function AddAssetSuccessPanel({
  headline,
  itemLabel,
  verifiedByTrueRead,
  onAddAnother,
}: AddAssetSuccessPanelProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Focus moves to the success headline on every mount - this component is
  // only ever mounted for the duration of a capture-success, so "on mount"
  // is exactly "when the panel appears".
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // SIL-1812: profile completion is deferred to the Exchange (the buy/sell
  // pages route an incomplete profile to /individual-profile), so this screen
  // no longer pushes it: "View in portfolio" is always the primary action.
  const addAnotherButton = (
    <Button
      variant="secondary"
      className="h-11 flex-1 font-semibold"
      onClick={onAddAnother}
    >
      Add another asset
    </Button>
  );
  const viewInPortfolioLink = (
    <Link
      to="/assets"
      className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700"
    >
      View in portfolio <ArrowRight className="h-4 w-4" />
    </Link>
  );

  return (
    <div
      role="status"
      aria-live="polite"
      className="mx-auto flex max-w-2xl flex-col items-center gap-4 rounded-xl border border-border bg-card p-8 text-center shadow-sm"
    >
      <span
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 animate-[success-mark-scale-in_400ms_ease-out] motion-reduce:animate-none"
      >
        <CircleCheckBig className="h-9 w-9" />
      </span>

      <h2
        ref={headingRef}
        tabIndex={-1}
        className="text-xl font-bold text-foreground focus:outline-none"
      >
        {headline}
      </h2>

      <p className="text-sm font-semibold text-foreground">{itemLabel}</p>

      {verifiedByTrueRead && (
        <span className="inline-flex items-center gap-1.5 rounded-md bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700 ring-1 ring-inset ring-brand-200">
          <BadgeCheck className="h-3.5 w-3.5" /> Verified by TrueRead probe
        </span>
      )}

      <p className="max-w-md text-sm text-muted-foreground">
        Verification is running. Your SX Report and Silicon Passport will be ready shortly.
      </p>

      <div className="mt-2 flex w-full flex-col gap-2.5 sm:flex-row">
        {viewInPortfolioLink}
        {addAnotherButton}
      </div>

      <SerialEntryPrompt />
    </div>
  );
}
